import { Xmark } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { Flex, Grid } from "styled-system/jsx";
import { Button, Select, Text } from "@ttc3k/trekker";
import { CopyToClipboard } from "shared/components/molecules/CopyToClipboard/CopyToClilpboard";
import { useFormContext, useWatch } from "react-hook-form";
import {
  PersonContactInfoFormValues,
  PhoneWithExtValues,
} from "../../../../usePersonContactInfoForm";
import {
  ControlledInput,
  ControlledSelectRoot,
} from "shared/components/formControl";
import { useEffect, useMemo } from "react";
import { EnumPeopleVisibleToMetadataPhonesType } from "gql/generated";

export const PhoneNumbers = () => {
  const { t } = useTranslation("people", {
    keyPrefix: "PERSON_CONTACT_INFO_MODAL.ADDITIONAL_CONTACT_INFO.LABELS",
  });

  const { control, formState, setValue } =
    useFormContext<PersonContactInfoFormValues>();
  const phones = useWatch({ control, name: "phones" });

  // Ensure there is at least one phone number field visible
  useEffect(() => {
    if (phones?.length === 0) {
      setValue("phones", [{ phone: "", ext: "", type: undefined }]);
    }
  }, [phones, setValue]);

  const phoneTypesCollection = useMemo(
    () =>
      Select.createCollection({
        items: Object.values(EnumPeopleVisibleToMetadataPhonesType).map(
          value => ({
            label: t(`PHONE_TYPES.${value}`),
            value,
          }),
        ),
      }),
    [t],
  );

  return (
    <Flex direction="column" gap="200">
      <Text visual="smallLabel">{t("PHONE_NUMBERS")}</Text>
      {/* Phone inputs */}
      <Flex direction="column" gap="150">
        {phones?.map((phoneEntry: PhoneWithExtValues, index: number) => (
          <Flex direction="column" key={`phone-${index}`}>
            {index === 0 && (
              <Text visual="smallTag" color="text.mid" mb="75">
                {t("PHONE")}
              </Text>
            )}
            <Flex direction="column" gap="50">
              <ControlledSelectRoot
                name={`phones.${index}.type`}
                placeholder={t("PHONE_TYPE_PLACEHOLDER")}
                collection={phoneTypesCollection}
                value={[phoneEntry.type ?? ""]}
                onValueChange={({ value }: { value: string[] }) =>
                  setValue(
                    `phones.${index}.type`,
                    value[0] as EnumPeopleVisibleToMetadataPhonesType,
                    { shouldDirty: true },
                  )
                }
                mt="50"
                css={{
                  '& [data-part="value-text"]': {
                    color: phoneEntry.type ? "text.dark" : "text.light",
                  },
                }}
              >
                {phoneTypesCollection.items.map(phoneType => (
                  <Select.Item key={phoneType.value} item={phoneType} />
                ))}
              </ControlledSelectRoot>
              <Grid
                gridTemplateColumns="1fr 80px auto auto"
                gridColumnGap="150"
                alignItems="start"
              >
                <ControlledInput
                  name={`phones.${index}.phone`}
                  type="tel"
                  placeholder="555-444-3333"
                  inputMode="numeric"
                  aria-label={`${t("PHONE")} ${index + 1}`}
                />
                <ControlledInput
                  name={`phones.${index}.ext`}
                  placeholder="123456"
                  inputMode="numeric"
                  aria-label={`${t("PHONE_EXT")} ${index + 1}`}
                />
                <CopyToClipboard
                  text={
                    phoneEntry.ext
                      ? `${phoneEntry.phone} ext. ${phoneEntry.ext}`
                      : phoneEntry.phone
                  }
                  iconOnly
                  iconSize="md"
                  iconCopyColor="icon.mid"
                  buttonProps={{
                    variant: "ghost",
                    visual: "primary",
                    borderRadius: "100",
                    px: "10px",
                    py: "10px",
                    mode: "light",
                    disabled:
                      formState.errors.phones?.[index] || !phones[index]?.phone,
                    marginTop: "5px",
                    h: "38px",
                  }}
                  aria-label={`${t("COPY_PHONE_NUMBER")} ${index + 1}`}
                />
                <Button
                  variant="ghost"
                  mode="light"
                  Icon={Xmark}
                  onClick={() =>
                    setValue(
                      "phones",
                      phones.filter(
                        (_: PhoneWithExtValues, i: number) => i !== index,
                      ),
                      { shouldDirty: true },
                    )
                  }
                  disabled={phones.length === 1 && index === 0}
                  marginTop="5px"
                  h="38px"
                  aria-label={`${t("DELETE_PHONE_NUMBER")} ${index + 1}`}
                  type="button"
                />
              </Grid>
            </Flex>
          </Flex>
        ))}
        {/* Add phone button */}
        <Button
          alignSelf="flex-start"
          visual="unstyled"
          size="sm"
          color={{
            base: "text.accent.blue.mid",
            hover: "text.accent.blue.dark",
            focusVisible: "text.accent.blue.dark",
          }}
          onClick={() =>
            setValue(
              "phones",
              [
                ...((phones ?? []) as PhoneWithExtValues[]),
                { phone: "", ext: "" },
              ],
              { shouldDirty: true },
            )
          }
          type="button"
        >
          {t("ADD_PHONE_NUMBER")}
        </Button>
      </Flex>
    </Flex>
  );
};
